/**
 * Sidebar Controller
 * Manages sidebar behavior including pin/unpin and mobile overlay
 */

import { Tooltip } from '../utils/Tooltip.js';

const SidebarState = {
  COLLAPSED: 'collapsed',
  PINNED: 'pinned'
};

/**
 * Label for the search shortcut shown in the header search tooltip. The
 * handler binds metaKey and ctrlKey alike, so this is purely about naming the
 * one the reader's keyboard actually has.
 * @constant {string}
 */
const SEARCH_SHORTCUT_LABEL =
  /Mac|iPhone|iPad|iPod/.test(navigator.userAgentData?.platform || navigator.platform || '')
    ? '⌘ K'
    : 'Ctrl + K';

export class Sidebar {
  /**
   * @param {Object} elements - DOM element references
   * @param {HTMLElement} elements.sidebar - Sidebar element
   * @param {HTMLElement} elements.sidebarToggle - Toggle button element
   * @param {HTMLElement} elements.sidebarBackdrop - Backdrop element (mobile)
   */
  constructor(elements) {
    this.sidebar = elements.sidebar;
    this.sidebarToggle = elements.sidebarToggle;
    this.sidebarBackdrop = elements.sidebarBackdrop;
    // Header search control — expanded-only, so it also needs the Cmd/Ctrl+K
    // shortcut and an inverted tooltip rule (see the two methods below).
    this.searchButton = this.sidebar?.querySelector('#sidebar-search') || null;

    // Namespaces the persisted pin preference per-user so it doesn't bleed
    // across accounts sharing a browser profile (matches the recents sections'
    // per-user localStorage convention).
    this.userId = elements.userId || null;

    // Single source of truth for sidebar state
    this.state = SidebarState.COLLAPSED;

    this.initialize();
  }

  /**
   * Initialize sidebar behavior
   */
  initialize() {
    // Toggle button click
    if (this.sidebarToggle) {
      this.sidebarToggle.addEventListener('click', () => {
        this.toggle();
      });
    }

    // Close sidebar on backdrop click (mobile)
    if (this.sidebarBackdrop) {
      this.sidebarBackdrop.addEventListener('click', () => {
        this.close();
      });
    }

    // Close sidebar on mobile when any menu item is clicked
    this.sidebar.addEventListener('click', (e) => {
      if (e.target.closest('.sidebar__item') && this.isMobile()) {
        this.close();
      }
    });

    // Initialize tooltips
    this.initializeTooltips();

    // Cmd/Ctrl+K → /search (the header control is expanded-only)
    this._initializeSearchShortcut();

    // Initialize sidebar__expand expand interaction
    this.initializeExpandArea();

    // Re-apply a persisted "pinned" preference so the sidebar returns the way
    // the user left it on this device (desktop only — see the method).
    this.restorePinnedPreference();

    // Listen for sidebar toggle events (from menu buttons in header/toolbar)
    if (window.eventBus) {
      // Couple-gate the Notes item live: on unlink, hide it and, if
      // the user is looking at /notes right now, route them home.
      // (There is no partner:linked counterpart — linking completes
      // server-side, so the item appears on the next load's bootstrap.)
      window.eventBus.on('partner:unlinked', () => {
        this.hideItem('notes');
        if (window.app?.modules?.pageManager?.getCurrentPage?.() === 'notes') {
          window.eventBus.emit('navigate', { page: 'dashboard' });
        }
      });

      window.eventBus.on('sidebar:toggle', () => {
        this.toggle();
      });

      // Close the mobile overlay when a non-sidebar source (e.g. ProfilePopup)
      // triggers navigation that would otherwise leave the overlay covering
      // the new page.
      window.eventBus.on('sidebar:close-mobile', () => {
        if (this.isMobile()) {
          this.close();
        }
      });

      // Listen for navigation notification updates
      window.eventBus.on('badge:navigation-update', (data) => {
        this.setNavigationState(data.badgeId, {
          count: data.count,
          indicator: data.indicator,
          tag: data.tag
        });
      });

      // Backward-compatible badge update events
      window.eventBus.on('badge:update', (data) => {
        this.setBadge(data.badgeId, {
          show: data.show,
          text: data.text,
          color: data.color || data.type
        });
      });

      // Listen for feature toggle events
      window.eventBus.on('toggle:sidebarHistory', (data) => {
        this.setItemVisibility('history', data.visible);
      });

      // Content-presence disclosure: a single event carries visibility for
      // every content-navigation item (FeatureToggleService._resolveSidebarContent).
      // New Chat is deliberately absent — it is never hideable.
      window.eventBus.on('toggle:sidebarContent', (data) => {
        this.setItemVisibility('search', data.search);
        this.setItemVisibility('discovery', data.discovery);
        this.setItemVisibility('practices', data.practices);
        this.setItemVisibility('insights', data.library);
        this.setItemVisibility('notes', data.notes);
      });
    }
  }

  /**
   * Initialize tooltip behavior for sidebar items
   */
  initializeTooltips() {
    // Find all elements with data-tooltip attribute
    const tooltipElements = this.sidebar.querySelectorAll('[data-tooltip]');

    tooltipElements.forEach(element => {
      const isToggleButton = element === this.sidebarToggle;
      // The header search button is the one item whose tooltip rule inverts:
      // it only EXISTS while expanded (collapsed it's display:none), and being
      // label-less it needs the tooltip precisely then. Every nav row below is
      // the opposite — labelled when expanded, so its tooltip is the collapsed-
      // only stand-in for the missing label.
      const isSearchButton = element === this.searchButton;

      Tooltip.setup(element, {
        position: 'right',
        // Tag the toggle's tooltip so updateVisualState can rewrite its text
        // in place without touching any other visible tooltip.
        className: isToggleButton ? 'tooltip--sidebar-toggle' : null,
        // Search is the only sidebar tooltip with a keyboard shortcut to
        // advertise, so it's the only one rendered as HTML — the shortcut is
        // set in a recessive grey (`.tooltip__shortcut`) so it reads as an
        // annotation on the label rather than part of it.
        html: isSearchButton,
        getText: isSearchButton
          ? () => `Search <span class="tooltip__shortcut">${SEARCH_SHORTCUT_LABEL}</span>`
          : null,
        shouldShow: () => {
          // Toggle button always shows tooltip
          if (isToggleButton) return true;
          if (isSearchButton) return this.state !== SidebarState.COLLAPSED;
          // Other items only show tooltip when sidebar is collapsed
          return this.state === SidebarState.COLLAPSED;
        }
      });
    });
  }

  /**
   * Bind the global Cmd/Ctrl+K search shortcut.
   *
   * This is not a convenience — it's the load-bearing half of moving Search out
   * of the nav list and into the header. The header control is expanded-only,
   * and collapsed is the default state (see `restorePinnedPreference`), so
   * without this there would be no route to /search at all until the user
   * expanded the sidebar.
   *
   * Implemented by forwarding to the button's own click so navigation, the
   * mobile overlay auto-close, and the active-state sweep all stay in their one
   * existing handler (app.js `initializeNavigation`) rather than being
   * re-implemented here. The button's content gate is honoured by refusing to
   * fire while it's hidden — `feature-toggle--hidden` means the user has nothing
   * to search yet (FeatureToggleService._resolveSidebarContent), and a shortcut
   * into an empty page is worse than no shortcut.
   * @returns {void}
   */
  _initializeSearchShortcut() {
    if (!this.searchButton) return;

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'k' && e.key !== 'K') return;
      if (!(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey) return;
      if (this.searchButton.classList.contains('feature-toggle--hidden')) return;

      e.preventDefault();
      this.searchButton.click();
    });
  }

  /**
   * Initialize the `.sidebar__expand` dead-zone interaction.
   *
   * The zone is symmetric: collapsed it is the rail below the nav and clicking
   * it expands; pinned (desktop only — see layout.css) it is the blank space
   * between a short Recents list and the profile footer, and clicking it
   * collapses. Both directions run through `toggle()` and share one hover
   * treatment, so the two halves cannot drift apart.
   */
  initializeExpandArea() {
    const expandSection = this.sidebar.querySelector('.sidebar__expand');
    if (!expandSection) return;

    let expandTooltip = null;

    // Drag guard. The zone is a large inert-looking area, so a click there is
    // not always a click: releasing a text selection or a scrollbar drag inside
    // it would otherwise toggle the sidebar. Require the press and the release
    // to be effectively co-located before treating it as intent. `pressPoint`
    // stays null when the press began outside the zone, which also covers a
    // drag that merely ENDS here.
    let pressPoint = null;
    const DRAG_SLOP_PX = 4;

    expandSection.addEventListener('pointerdown', (e) => {
      pressPoint = { x: e.clientX, y: e.clientY };
    });
    expandSection.addEventListener('pointercancel', () => {
      pressPoint = null;
    });

    expandSection.addEventListener('click', (e) => {
      const press = pressPoint;
      pressPoint = null;
      if (!press) return;
      if (Math.abs(e.clientX - press.x) > DRAG_SLOP_PX) return;
      if (Math.abs(e.clientY - press.y) > DRAG_SLOP_PX) return;

      this.toggle();
    });

    // Hover feedback. Runs in BOTH states — the pinned half is what makes the
    // collapse affordance discoverable, since blank space advertises nothing on
    // its own. No state guard is needed for mobile: pinned below 1020px the zone
    // is display:none (so mouseenter cannot fire) and collapsed the whole sidebar
    // is translated off-screen.
    expandSection.addEventListener('mouseenter', () => {
      this.sidebar.classList.add('sidebar--expand-hover');
      this.sidebarToggle.classList.add('sidebar__toggle--hover');

      // Show tooltip for toggle button
      const tooltipText = this.sidebarToggle.getAttribute('data-tooltip');
      if (tooltipText) {
        // Read the text and placement off the attributes rather than hardcoding
        // them, so the dead-zone path can't drift from the direct-hover path.
        // `updateVisualState` keeps both in step with the state, which is what
        // makes this one handler read "Open sidebar"/right when collapsed and
        // "Close sidebar"/below when pinned.
        const position = this.sidebarToggle.getAttribute('data-tooltip-position') || 'right';
        expandTooltip = Tooltip.create(this.sidebarToggle, tooltipText, position, false, 'tooltip--sidebar-toggle');
      }
    });

    // Clear the hover tint + expand tooltip. Used both on mouseleave and
    // defensively on transition-to-pinned (see setState): expanding grows
    // Recents down into the zone, so the zone shrinks out from under a
    // stationary cursor and the browser may not synthesize a mouseleave. This
    // guards against a lingering tint/tooltip. It deliberately does NOT re-arm
    // when the pointer is still inside the smaller pinned zone — re-tinting the
    // instant you clicked would read as a flash, so the tint returns on the next
    // deliberate mouseleave/mouseenter.
    const clearExpandHover = () => {
      this.sidebar.classList.remove('sidebar--expand-hover');
      this.sidebarToggle?.classList.remove('sidebar__toggle--hover');

      if (expandTooltip) {
        Tooltip.remove(expandTooltip);
        expandTooltip = null;
      }
    };
    this._clearExpandHover = clearExpandHover;

    expandSection.addEventListener('mouseleave', clearExpandHover);
  }

  /**
   * Toggle sidebar pinned state
   */
  toggle() {
    if (this.state === SidebarState.PINNED) {
      // Already pinned → collapse
      this.setState(SidebarState.COLLAPSED);
    } else {
      // Collapsed → pin it open
      this.setState(SidebarState.PINNED);
    }
  }

  /**
   * Pin sidebar open
   */
  pin() {
    this.setState(SidebarState.PINNED);
  }

  /**
   * Unpin sidebar (collapse)
   */
  unpin() {
    this.setState(SidebarState.COLLAPSED);
  }

  /**
   * Close sidebar (for mobile)
   */
  close() {
    this.setState(SidebarState.COLLAPSED);
    this.sidebar.classList.remove('sidebar--open');
  }

  /**
   * Check if sidebar is pinned
   * @returns {boolean}
   */
  isPinned() {
    return this.state === SidebarState.PINNED;
  }

  /**
   * Check if viewport is mobile width
   * @returns {boolean}
   */
  isMobile() {
    return window.innerWidth < 1020;
  }

  /**
   * Get current sidebar state
   * @returns {string} Current state (collapsed, hover, or pinned)
   */
  getState() {
    return this.state;
  }

  /**
   * Get sidebar element
   * @returns {HTMLElement}
   */
  getElement() {
    return this.sidebar;
  }

  /**
   * Open sidebar to specific section programmatically
   * @param {string} sectionName - Name of the section to open (e.g., 'discovery')
   */
  openToSection(sectionName) {
    // Open sidebar if closed
    if (this.state !== SidebarState.PINNED) {
      this.pin();
    }

    // Switch to requested section by clicking the appropriate section button
    const sectionButton = this.sidebar.querySelector(`[data-section="${sectionName}"]`);
    if (sectionButton) {
      sectionButton.click();
      console.log(`[Sidebar] Opened to section: ${sectionName}`);
    } else {
      console.warn(`[Sidebar] Section not found: ${sectionName}`);
    }
  }

  /**
   * Set sidebar state and update visual presentation
   * @param {string} newState - New state (use SidebarState constants)
   */
  setState(newState) {
    const wasPinned = this.state === SidebarState.PINNED;
    this.state = newState;
    this.updateVisualState();

    // On transition to pinned, Recents grows down into `.sidebar__expand` in the
    // same synchronous recalc, so the zone shrinks (often to nothing) under a
    // still-stationary cursor; clear any stuck hover tint / expand tooltip that
    // would otherwise be left behind (see initializeExpandArea).
    if (newState === SidebarState.PINNED) {
      this._clearExpandHover?.();
    }

    // Collapsing: the pointer that just clicked the toggle is still over it
    // (collapsed, the toggle spans the full rail), so the collapsed :hover swap
    // would flash the panel glyph over the brand mark until the shrinking
    // sidebar slides out from under the cursor. Suppress the swap for the
    // duration of the collapse (see `.sidebar--collapsing` in sidebar.css).
    if (newState === SidebarState.COLLAPSED && wasPinned) {
      this._suppressCollapseHover();
    }

    this._persistPinned();
  }

  /**
   * Flag the sidebar as collapsing so the collapsed hover-swap is suppressed
   * until the close animation settles. Cleared on the width transition's end,
   * with a timeout fallback for cases where it never fires (reduced motion, an
   * interrupted transition, or a width that didn't actually change).
   * @returns {void}
   */
  _suppressCollapseHover() {
    this.sidebar.classList.add('sidebar--collapsing');

    clearTimeout(this._collapseTimer);
    if (this._collapseEndHandler) {
      this.sidebar.removeEventListener('transitionend', this._collapseEndHandler);
    }

    const done = () => {
      this.sidebar.classList.remove('sidebar--collapsing');
      clearTimeout(this._collapseTimer);
      if (this._collapseEndHandler) {
        this.sidebar.removeEventListener('transitionend', this._collapseEndHandler);
        this._collapseEndHandler = null;
      }
    };

    this._collapseEndHandler = (e) => {
      if (e.target === this.sidebar && (e.propertyName === 'width' || e.propertyName === 'transform')) {
        done();
      }
    };
    this.sidebar.addEventListener('transitionend', this._collapseEndHandler);

    // --duration-slow is 300ms; leave headroom before force-clearing.
    this._collapseTimer = setTimeout(done, 450);
  }

  /**
   * Update CSS classes and ARIA attributes based on current state
   */
  updateVisualState() {
    // Remove all state classes
    this.sidebar.classList.remove('sidebar--pinned');

    // Add appropriate class for current state
    if (this.state === SidebarState.PINNED) {
      this.sidebar.classList.add('sidebar--pinned');
    }

    // Update ARIA attribute
    this.sidebarToggle?.setAttribute('aria-expanded',
      this.state === SidebarState.PINNED);

    // Update the toggle's tooltip + accessible name based on state. The
    // aria-label carries the accessible name for keyboard users (the shared
    // tooltip utility is mouse-only, so keyboard focus reveals the icon swap
    // but no tooltip).
    const labelText = this.state === SidebarState.PINNED
      ? 'Close sidebar'
      : 'Open sidebar';
    this.sidebarToggle?.setAttribute('data-tooltip', labelText);
    this.sidebarToggle?.setAttribute('aria-label', labelText);

    // Expanded, the toggle is one of a pair of header controls and tooltips
    // below match its neighbour. Collapsed, it sits at the top of a narrow rail
    // with the first nav item directly beneath it, so below would cover that
    // item — anchor right instead. Tooltip.js re-reads this on each hover.
    this.sidebarToggle?.setAttribute(
      'data-tooltip-position',
      this.state === SidebarState.PINNED ? 'below' : 'right'
    );

    // If the toggle's own tooltip is currently visible (e.g. the user clicked
    // it while hovering), update its text in place. Scoped to the toggle's
    // tooltip via its marker class so unrelated visible tooltips are untouched.
    document.querySelectorAll('.tooltip--sidebar-toggle.tooltip--visible')
      .forEach(tooltip => { tooltip.textContent = labelText; });
  }

  // ========================================
  // PIN-STATE PERSISTENCE
  // ========================================

  /**
   * localStorage key for this user's persisted pin preference. Per-user (with
   * a shared fallback when no user id is available) so the preference doesn't
   * bleed across accounts sharing a browser profile.
   * @returns {string}
   */
  _pinStorageKey() {
    return `sidebar_pinned_${this.userId || 'shared'}`;
  }

  /**
   * Read the persisted pin preference. Returns PINNED only when explicitly
   * stored as pinned; anything else — unset, collapsed, or a read error
   * (e.g. storage disabled) — resolves to COLLAPSED.
   * @returns {string} A SidebarState value.
   */
  _readPinnedPreference() {
    try {
      return localStorage.getItem(this._pinStorageKey()) === '1'
        ? SidebarState.PINNED
        : SidebarState.COLLAPSED;
    } catch {
      return SidebarState.COLLAPSED;
    }
  }

  /**
   * Persist the current pin state. No-op on mobile: there the sidebar is a
   * transient overlay (open/close) whose state must not clobber the desktop
   * pin preference. Storage failures (private mode / quota) degrade silently —
   * a sidebar that doesn't remember its state is cosmetic, not worth surfacing.
   */
  _persistPinned() {
    if (this.isMobile()) return;
    try {
      localStorage.setItem(
        this._pinStorageKey(),
        this.state === SidebarState.PINNED ? '1' : '0'
      );
    } catch {
      /* storage unavailable — non-fatal */
    }
  }

  /**
   * On load, re-apply a persisted "pinned" preference so the sidebar returns
   * the way the user left it. Desktop only — on mobile the sidebar is an
   * overlay and auto-opening it on every load would be intrusive. The width
   * transition is suppressed for the restore frame (via `.sidebar--no-anim`)
   * so a restored-open sidebar appears already expanded instead of sliding
   * open on every navigation.
   * @returns {void}
   */
  restorePinnedPreference() {
    if (this.isMobile()) return;
    if (this._readPinnedPreference() !== SidebarState.PINNED) return;

    this.sidebar.classList.add('sidebar--no-anim');
    this.setState(SidebarState.PINNED);
    // Force a reflow so the no-anim state paints before transitions return.
    void this.sidebar.offsetWidth;
    const raf = window.requestAnimationFrame || ((cb) => setTimeout(cb, 0));
    raf(() => this.sidebar.classList.remove('sidebar--no-anim'));
  }

  // ========================================
  // BADGE METHODS
  // ========================================

  /**
   * Set sidebar count and notification indicator for a navigation item.
   * @param {string} badgeId - The navigation identifier
   * @param {Object} options - Navigation metadata
   * @param {number|null} [options.count] - Informational count
   * @param {{color: string}|null} [options.indicator] - Notification indicator
   */
  setNavigationState(badgeId, options = {}) {
    const { count = null, indicator = null, tag = null } = options;

    const button = this.sidebar.querySelector(`[data-badge-id="${badgeId}"]`);
    if (!button) {
      console.warn(`[Sidebar] Badge button not found: ${badgeId}`);
      return;
    }

    const dots = button.querySelectorAll('.sidebar__badge-dot');
    const countEl = button.querySelector('.sidebar__item-count');
    const colorClasses = ['sidebar__badge-dot--orange', 'sidebar__badge-dot--red', 'sidebar__badge-dot--blue'];

    dots.forEach(dot => {
      dot.classList.remove(...colorClasses);
      if (indicator?.color) {
        dot.classList.add(`sidebar__badge-dot--${indicator.color}`);
      }
    });

    button.classList.toggle('sidebar__item--badge-active', !!indicator);

    if (countEl) {
      // Zero is hidden, not rendered as "0": an empty-state count is noise in a
      // calm-notification sidebar. Applies to every counted item (today
      // Practices and Notes; discovery/insights pass null).
      const showCount = Number.isFinite(count) && count > 0;
      countEl.textContent = showCount ? String(count) : '';
      countEl.hidden = !showCount;
    }
  }

  /**
   * Set badge visibility, text, and color for a sidebar item
   * @param {string} badgeId - The badge identifier (e.g., 'discovery', 'insights')
   * @param {Object} options - Badge options
   * @param {boolean} options.show - Whether to show the badge
   * @param {string} [options.text='NEW'] - The text to display in the tag (when expanded)
   * @param {string} [options.color='blue'] - The badge color ('blue', 'red', 'orange')
   */
  setBadge(badgeId, options = {}) {
    const { show = false, text = 'NEW', color = 'blue' } = options;

    this.setNavigationState(badgeId, {
      indicator: show ? { color } : null,
      tag: show ? { text, color } : null
    });
  }

  /**
   * Show a badge with optional text and color
   * @param {string} badgeId - The badge identifier
   * @param {string} [text='NEW'] - The text to display
   * @param {string} [color='blue'] - The badge color
   */
  showBadge(badgeId, text = 'NEW', color = 'blue') {
    this.setBadge(badgeId, { show: true, text, color });
  }

  /**
   * Hide a badge
   * @param {string} badgeId - The badge identifier
   */
  hideBadge(badgeId) {
    this.setBadge(badgeId, { show: false });
  }

  // ========================================
  // ITEM VISIBILITY METHODS
  // ========================================

  /**
   * Set visibility of a sidebar item
   * @param {string} itemId - The item identifier ('history', 'search',
   *   'discovery', 'practices', 'insights', 'notes')
   * @param {boolean} visible - Whether the item should be visible
   */
  setItemVisibility(itemId, visible) {
    let button;

    switch (itemId) {
      case 'history':
        // The sidebar recents block (the Recents collapsible
        // sections, populated by SidebarRecentsController) — hidden as a
        // whole during onboarding steps that set sidebarHistory: false.
        button = this.sidebar.querySelector('#sidebar-recents-sections');
        break;
      case 'notes':
        // Couple-gated: hidden in markup, revealed by the content resolver
        // (FeatureToggleService) for linked users when not in orientation.
        button = this.sidebar.querySelector('[data-page="notes"]');
        break;
      case 'search':
      case 'discovery':
      case 'practices':
      case 'insights':
        // Content-presence-gated nav items (Search / Discovery / Practices /
        // Library), driven by `toggle:sidebarContent`.
        button = this.sidebar.querySelector(`[data-page="${itemId}"]`);
        break;
      default:
        console.warn(`[Sidebar] Unknown item: ${itemId}`);
        return;
    }

    if (!button) {
      console.warn(`[Sidebar] Item button not found: ${itemId}`);
      return;
    }

    if (visible) {
      button.classList.remove('feature-toggle--hidden');
    } else {
      button.classList.add('feature-toggle--hidden');
    }
  }

  /**
   * Show a sidebar item
   * @param {string} itemId - The item identifier
   */
  showItem(itemId) {
    this.setItemVisibility(itemId, true);
  }

  /**
   * Hide a sidebar item
   * @param {string} itemId - The item identifier
   */
  hideItem(itemId) {
    this.setItemVisibility(itemId, false);
  }

}
